import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Barbell, Trophy, Heartbeat, ChartLineUp, Medal, CheckCircle, UsersThree, Fire,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const WINDOWS = [
  { k: "7d", label: "W" },
  { k: "30d", label: "M" },
  { k: "1y", label: "Y" },
  { k: "all", label: "ALL" },
];

const COLUMNS = [
  { k: "composite", label: "SCORE", fmt: (v) => v != null ? v : "—", right: true, accent: true },
  { k: "workouts", label: "WORKOUTS", fmt: (v) => v ?? 0, right: true },
  { k: "tonnage", label: "TONNAGE", fmt: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : (v ?? 0), right: true },
  { k: "prs", label: "PRS", fmt: (v) => v ?? 0, right: true },
  { k: "test_pbs", label: "TEST PB", fmt: (v) => v ?? 0, right: true },
  { k: "completed_sessions", label: "COMPLETED", fmt: (v, r) => `${v ?? 0}/${r.planned_sessions ?? 0}`, right: true },
  { k: "adherence_pct", label: "ADHERENCE", fmt: (v) => v == null ? "—" : `${v}%`, right: true, bar: true, max: 100 },
  { k: "avg_readiness", label: "READINESS", fmt: (v) => v == null ? "—" : v, right: true, bar: true, max: 100 },
];

export default function CoachPerformance() {
  const [window, setWindow] = useState("30d");
  const [teamId, setTeamId] = useState("");
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState("composite");
  const [sortDir, setSortDir] = useState("desc"); // desc | asc

  useEffect(() => {
    const q = new URLSearchParams({ window });
    if (teamId) q.set("team_id", teamId);
    api.get(`/coach/performance?${q.toString()}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [window, teamId]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.rows];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const changeSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "client_name" ? "asc" : "desc"); }
  };

  if (!data) return <div className="p-10 label-tiny text-text-secondary">Loading...</div>;

  const totals = data.totals || {};

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10" data-testid="coach-performance-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// ROSTER PERFORMANCE</div>
        <h1 className="font-heading text-4xl sm:text-5xl">PERFORMANCE</h1>
        <p className="text-text-secondary text-sm mt-2">Compare athletes side-by-side. Windowed W/M/Y with team filtering.</p>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="performance-filters">
        <div className="flex items-center gap-1" data-testid="window-picker">
          {WINDOWS.map((w) => (
            <button
              key={w.k}
              onClick={() => setWindow(w.k)}
              data-testid={`window-${w.k}`}
              className={`px-3 py-1 text-[10px] label-tiny tracking-wider border ${window === w.k ? "bg-brand-accent text-surface-base border-brand-accent" : "border-slate-200 text-text-secondary hover:border-white/40"}`}
            >{w.label}</button>
          ))}
        </div>
        <div className="w-px h-6 bg-white/10 mx-1" />
        <div className="flex flex-wrap items-center gap-1" data-testid="team-picker">
          <button
            onClick={() => setTeamId("")}
            data-testid="team-all"
            className={`px-3 py-1 text-[10px] label-tiny tracking-wider border ${!teamId ? "bg-brand-accent text-surface-base border-brand-accent" : "border-slate-200 text-text-secondary hover:border-white/40"}`}
          >ALL ROSTER ({data.roster_size})</button>
          {(data.teams || []).map((t) => (
            <button
              key={t.team_id}
              onClick={() => setTeamId(t.team_id)}
              data-testid={`team-${t.team_id}`}
              className={`inline-flex items-center gap-1 px-3 py-1 text-[10px] label-tiny tracking-wider border ${teamId === t.team_id ? "bg-brand-accent text-surface-base border-brand-accent" : "border-slate-200 text-text-secondary hover:border-white/40"}`}
            >
              <UsersThree size={10} weight="fill" /> {t.name?.toUpperCase()} ({t.member_count})
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10 mb-8" data-testid="performance-totals">
        <Kpi label="ATHLETES" value={data.rows.length} icon={UsersThree} />
        <Kpi label="WORKOUTS" value={totals.workouts} icon={Barbell} />
        <Kpi label="TONNAGE" value={totals.tonnage >= 1000 ? `${(totals.tonnage / 1000).toFixed(1)}k` : totals.tonnage} icon={Fire} />
        <Kpi label="PRS" value={totals.prs} icon={Trophy} accent />
        <Kpi label="ADHERENCE" value={totals.adherence_pct == null ? "—" : `${totals.adherence_pct}%`} icon={CheckCircle} />
        <Kpi label="READINESS" value={totals.avg_readiness == null ? "—" : totals.avg_readiness} icon={Heartbeat} />
      </div>

      {/* Table */}
      {sortedRows.length === 0 ? (
        <div className="border border-dashed border-slate-200 py-14 text-center" data-testid="performance-empty">
          <ChartLineUp size={36} weight="duotone" className="text-brand-accent mx-auto mb-3" />
          <p className="text-sm text-text-secondary">No athletes in this filter.</p>
        </div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto" data-testid="performance-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 label-tiny text-[10px] text-text-secondary">
                <th className="px-3 py-2 text-left w-10">#</th>
                <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => changeSort("client_name")} data-testid="sort-client_name">
                  ATHLETE {sortKey === "client_name" && (sortDir === "asc" ? "▲" : "▼")}
                </th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.k}
                    onClick={() => changeSort(c.k)}
                    data-testid={`sort-${c.k}`}
                    className={`px-3 py-2 cursor-pointer select-none ${c.right ? "text-right" : "text-left"} hover:text-white`}
                  >
                    {c.label} {sortKey === c.k && (sortDir === "asc" ? "▲" : "▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedRows.map((r) => (
                <React.Fragment key={r.client_id}>
                <tr className="hover:bg-slate-50" data-testid={`perf-row-${r.client_id}`}>
                  <td className="px-3 py-2 text-text-secondary tabular-nums font-heading">{r.rank}</td>
                  <td className="px-3 py-2">
                    <Link to={`/coach/clients/${r.client_id}`} className="flex items-center gap-2 hover:text-brand-accent">
                      <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold">
                        {(r.client_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <span className="truncate font-heading">{r.client_name || "—"}</span>
                    </Link>
                  </td>
                  {COLUMNS.map((c) => {
                    const v = r[c.k];
                    return (
                      <td key={c.k} className={`px-3 py-2 tabular-nums ${c.right ? "text-right" : ""}`} data-testid={`perf-${r.client_id}-${c.k}`}>
                        {c.bar && v != null ? (
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-16 h-1 bg-white/10 overflow-hidden">
                              <div className={`h-full ${v >= 70 ? "bg-green-500" : v >= 40 ? "bg-brand-accent" : "bg-red-500"}`} style={{ width: `${Math.max(3, Math.min(100, v))}%` }} />
                            </div>
                            <span className={c.accent ? "text-brand-accent font-heading" : ""}>{c.fmt(v, r)}</span>
                          </div>
                        ) : (
                          <span className={c.accent ? "text-brand-accent font-heading text-base" : ""}>{c.fmt(v, r)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
                {r.latest_tests?.length > 0 && (
                  <tr className="border-t border-dashed border-slate-100" data-testid={`perf-tests-row-${r.client_id}`}>
                    <td></td>
                    <td colSpan={COLUMNS.length + 1} className="px-3 pb-3 pt-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="label-tiny text-[9px] text-text-secondary mr-1">LATEST TESTS</span>
                        {r.latest_tests.map((t, i) => {
                          const pct = t.percent_change;
                          const higher = t.higher_is_better !== false;
                          const improved = pct != null && ((pct > 0) === higher);
                          const chipCls = pct == null ? "border-slate-200 text-slate-600"
                            : improved ? "border-green-300 text-green-700 bg-green-50"
                            : "border-red-300 text-red-700 bg-red-50";
                          return (
                            <span
                              key={i}
                              className={`text-[10px] label-tiny tracking-wider px-2 py-0.5 border ${chipCls} ${t.is_personal_best ? "font-bold" : ""}`}
                              data-testid={`perf-test-chip-${r.client_id}-${i}`}
                              title={`${t.test_name} · ${t.test_date}`}
                            >
                              {t.test_name} {t.best_score}{t.unit ? ` ${t.unit}` : ""}
                              {pct != null && ` (${pct > 0 ? "+" : ""}${pct}%)`}
                              {t.is_personal_best && " ★"}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-surface-DEFAULT p-4" data-testid={`kpi-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={14} weight="duotone" className={accent ? "text-yellow-400" : "text-brand-primary"} />
        <span className="label-tiny text-[9px] text-text-secondary">{label}</span>
      </div>
      <div className={`font-heading text-2xl tabular-nums ${accent ? "text-yellow-400" : "text-brand-accent"}`}>{value}</div>
    </div>
  );
}
